// `/forgot-password` — запрос на сброс пароля (OTP-флоу).
// Source: docs/specs/auth-otp-verification (reset-флоу).
//
// Submit → POST /auth/forgot-password (anti-enum: всегда 200) → кладём email в
// PasswordResetContext → /verify-reset-code (ввод кода). На несуществующий email
// тоже ведём на экран кода (не раскрываем наличие аккаунта) — там код просто не
// придёт. flushSync перед navigate — context должен примениться до redirect-guard'а.

import { useLocation, Link } from 'wouter';
import { flushSync } from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import { ForgotPasswordSchema, type ForgotPasswordForm } from '@/api/schemas';
import { useForgotPasswordMutation } from '@/api/hooks/auth';
import { applyValidationErrors, useApiErrorToast } from '@/api/error';
import { usePasswordResetContext } from '@/context/PasswordResetContext';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { FormField } from '@/components/forms/FormField';
import { SubmitButton } from '@/components/forms/SubmitButton';

export function ForgotPassword() {
  const [, setLocation] = useLocation();
  const mutation = useForgotPasswordMutation();
  const passwordReset = usePasswordResetContext();
  const showErrorToast = useApiErrorToast();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = handleSubmit(async (form) => {
    try {
      await mutation.mutateAsync(form);
      flushSync(() => {
        passwordReset.set({ email: form.email });
      });
      setLocation('/verify-reset-code');
    } catch (err) {
      const applied = applyValidationErrors<keyof ForgotPasswordForm>(err, setError);
      if (!applied) showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <ScreenHeader onBack={() => setLocation('/signin')} title="Назад" />

      {/* Gradient-hero + blur-blob (палитра лендинга, как на /verify-code). */}
      <div className="relative flex-1 overflow-y-auto overflow-x-hidden bg-gradient-to-b from-primary/5 to-transparent px-6 pb-8 pt-2">
        <div className="pointer-events-none absolute -right-16 -top-10 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">
          <div className="mb-7 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-surface bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-lg shadow-primary/25">
              <KeyRound className="h-8 w-8" strokeWidth={1.75} />
            </div>
            <h1 className="mb-1.5 text-hl-title font-bold leading-tight tracking-tight">
              Восстановление пароля
            </h1>
            <p className="text-sm text-muted-foreground">
              Введите email — отправим код для сброса
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="ваш@email.com"
              error={errors.email?.message}
              {...register('email')}
            />
            <SubmitButton isPending={mutation.isPending}>Отправить код</SubmitButton>
          </form>

          <p className="mt-5 text-center text-hl-small text-muted-foreground">
            <Link href="/signin" className="font-semibold text-primary-text">
              ← Назад ко входу
            </Link>
          </p>
        </div>
      </div>
    </MobileFrame>
  );
}
